import {
    CalendarClock,
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    ChevronRight,
    RotateCcw,
    SlidersHorizontal,
    Timer,
    TrendingUp,
} from "lucide-react";

import { useMemo, useState } from "react";
import {
    COACH_ASSIGNMENT_STATUSES,
    type CoachAssignment,
    type CoachAssignmentStatus,
} from "@flexv-coach/shared";
import { Alert } from "@/components/ui/Alert";
import { Card, CardContent, StatsCard } from "@/components/ui/Card";
import { MenuSelect } from "@/components/ui/Field";
import { EmptyState, ErrorState, LoadingBlock } from "@/components/ui/States";
import {
    addDays,
    formatDuration,
    formatRangeLabel,
    formatWeekday,
    startOfWeek,
    todayIso,
} from "@/lib/date";
import { ApiError } from "@/lib/api";
import { statusLabel } from "@/lib/labels";
import { useAuth } from "@/providers/AuthProvider";
import { useI18n } from "@/hooks/useI18n";
import { AssignmentDetailDrawer } from "./components/AssignmentDetailDrawer";
import { DayList, WeekGrid } from "./components/WeekGrid";
import { useCoachPlan, useUpdateAssignmentStatus } from "./useCoachPlan";

/**
 * Jadwal Coach — papan kalender MINGGUAN.
 *
 * Konsepnya berbeda dengan menu Penugasan (tabel): di sini yang dijawab
 * adalah "kapan saya mengajar minggu ini & bagaimana bentuk minggunya",
 * jadi fokusnya keterbacaan 7 hari penuh, bukan daftar panjang.
 */
export function PlanPage() {
    const { coach } = useAuth();
    const { t } = useI18n();

    const [anchor, setAnchor] = useState(() => startOfWeek(todayIso()));
    const [outletId, setOutletId] = useState("");
    const [status, setStatus] = useState<CoachAssignmentStatus | "">("");
    const [selected, setSelected] = useState<CoachAssignment | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    const from = anchor;
    const to = addDays(anchor, 6);

    const { data, isPending, isError, error, refetch } = useCoachPlan({
        from,
        to,
        outletId,
        status,
    });
    const updateStatus = useUpdateAssignmentStatus();

    const outletOptions = useMemo(() => {
        const map = new Map<string, string>();
        for (const affiliation of coach?.affiliations ?? []) {
            if (affiliation.outlet) map.set(affiliation.outlet.id, affiliation.outlet.name);
        }
        return [
            { value: "", label: t("plan.filter.allOutlets") },
            ...[...map].map(([value, label]) => ({ value, label })),
        ];
    }, [coach, t]);

    const statusOptions = [
        { value: "", label: t("plan.filter.allStatuses") },
        ...COACH_ASSIGNMENT_STATUSES.map((value) => ({ value, label: statusLabel(value) })),
    ];

    const summary = data?.summary;
    const isCurrentWeek = anchor === startOfWeek(todayIso());

    const goToWeek = (offset: number) => setAnchor((prev) => addDays(prev, offset * 7));

    const isFilterDirty = outletId !== "" || status !== "";

    const resetFilters = () => {
        setOutletId("");
        setStatus("");
    };

    const handleUpdateStatus = async (next: CoachAssignmentStatus) => {
        if (!selected) return;
        setActionError(null);
        try {
            const updated = await updateStatus.mutateAsync({
                assignmentId: selected.id,
                status: next,
            });
            setSelected(updated);
        } catch (caught) {
            setActionError(
                caught instanceof ApiError ? caught.message : t("plan.actionFailed"),
            );
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <header className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <h1 className="app-text-primary truncate text-2xl font-bold sm:text-3xl">
                            {t("plan.title")}
                        </h1>
                        <p className="app-shell-text-muted mt-1 truncate text-sm">
                            {t("plan.subtitle")} · {formatRangeLabel(from, to)}
                        </p>
                    </div>

                    {/* Navigasi minggu + filter: satu toolbar rapi, bukan
                        menempel longgar tanpa bingkai. Tombol "minggu ini"
                        tidak wrap (whitespace-nowrap) + min-width agar label
                        dua kata seperti "Minggu ini" tidak patah. */}
                    <nav
                        aria-label={t("plan.filter.title")}
                        className="app-content-subtle flex w-fit max-w-[62%] shrink-0 items-center gap-x-1.5 overflow-x-auto rounded-2xl border app-shell-border px-2.5 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    >
                        <button
                            type="button"
                            aria-label={t("plan.prevWeek")}
                            onClick={() => goToWeek(-1)}
                            className="app-shell-text-muted shrink-0 rounded-lg p-2 transition-colors hover:app-content-subtle-hover hover:text-[var(--app-text-primary)] [&>svg]:h-5 [&>svg]:w-5"
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </button>

                        <button
                            type="button"
                            onClick={() => setAnchor(startOfWeek(todayIso()))}
                            disabled={isCurrentWeek}
                            className={
                                isCurrentWeek
                                    ? "app-text-primary w-24 shrink-0 whitespace-nowrap rounded-lg px-2 py-2 text-center text-sm font-semibold"
                                    : "app-shell-text-muted w-24 shrink-0 whitespace-nowrap rounded-lg px-2 py-2 text-center text-sm font-semibold transition-colors hover:app-content-subtle-hover hover:text-[var(--app-text-primary)]"
                            }
                        >
                            {t("plan.thisWeek")}
                        </button>

                        <button
                            type="button"
                            aria-label={t("plan.nextWeek")}
                            onClick={() => goToWeek(1)}
                            className="app-shell-text-muted shrink-0 rounded-lg p-2 transition-colors hover:app-content-subtle-hover hover:text-[var(--app-text-primary)] [&>svg]:h-5 [&>svg]:w-5"
                        >
                            <ChevronRight className="h-4 w-4" />
                        </button>

                        <span aria-hidden className="app-shell-border mx-1 h-4 w-px shrink-0" />

                        <SlidersHorizontal
                            aria-hidden
                            className="app-shell-text-muted ml-1 h-5 w-5 shrink-0"
                        />
                        <MenuSelect
                            label={t("plan.filter.outlet")}
                            options={outletOptions}
                            value={outletId}
                            onChange={setOutletId}
                            className="w-36"
                        />

                        <MenuSelect
                            label={t("plan.filter.status")}
                            options={statusOptions}
                            value={status}
                            onChange={(next) => setStatus(next as CoachAssignmentStatus | "")}
                            className="w-32"
                        />

                        {isFilterDirty && (
                            <button
                                type="button"
                                onClick={resetFilters}
                                aria-label={t("plan.filter.reset")}
                                className="app-shell-text-muted shrink-0 rounded-lg p-2 transition-colors hover:app-content-subtle-hover hover:text-[var(--app-text-primary)] [&>svg]:h-5 [&>svg]:w-5"
                            >
                                <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </nav>
                </div>
            </header>

            {summary && (
                <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <StatsCard
                        label={t("plan.summary.sessions")}
                        value={summary.totalSessions}
                        icon={<CalendarDays className="h-5 w-5" />}
                        hint={t("plan.summary.sessionsHint", {
                            count: summary.byRole.lead_coach,
                        })}
                    />
                    <StatsCard
                        label={t("plan.summary.totalHours")}
                        value={formatDuration(summary.totalMinutes)}
                        icon={<Timer className="h-5 w-5" />}
                        hint={t("plan.summary.totalHoursHint")}
                    />
                    <StatsCard
                        label={t("plan.summary.completed")}
                        value={summary.byStatus.completed}
                        icon={<CheckCircle2 className="h-5 w-5" />}
                        hint={t("plan.summary.completedHint", {
                            count: summary.byStatus.scheduled,
                        })}
                    />
                    <StatsCard
                        label={t("plan.summary.busiestDay")}
                        value={
                            summary.busiestDay
                                ? `${formatWeekday(summary.busiestDay.date)} · ${
                                      summary.busiestDay.assignments.length
                                  }`
                                : "—"
                        }
                        icon={<TrendingUp className="h-5 w-5" />}
                        hint={
                            summary.busiestDay
                                ? formatDuration(summary.busiestDay.totalMinutes)
                                : t("plan.summary.busiestHint")
                        }
                    />
                </section>
            )}

            <Card>
                <CardContent className="flex flex-col gap-4">
                    {isPending && <LoadingBlock label={t("plan.loading")} />}

                    {isError && (
                        <ErrorState
                            message={
                                error instanceof ApiError ? error.message : t("plan.loadFailed")
                            }
                            onRetry={() => void refetch()}
                        />
                    )}

                    {data && (
                        <>
                            <WeekGrid days={data.days} onSelect={setSelected} />
                            <DayList days={data.days} onSelect={setSelected} />

                            {summary?.totalSessions === 0 && (
                                <EmptyState
                                    icon={<CalendarClock className="h-6 w-6" />}
                                    title={t("plan.empty.title")}
                                    description={t("plan.empty.description")}
                                />
                            )}
                        </>
                    )}

                    {actionError && <Alert tone="error">{actionError}</Alert>}
                </CardContent>
            </Card>

            <AssignmentDetailDrawer
                assignment={selected}
                onClose={() => {
                    setSelected(null);
                    setActionError(null);
                }}
                onUpdateStatus={handleUpdateStatus}
                updating={updateStatus.isPending}
            />
        </div>
    );
}
